import { Chip, Stack } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  ScoutNodeStatus,
  type ScoutNodesQuery,
  type ScoutNodesQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { prettyPrintDateTime } from "util/dates";

const SCOUT_NODES_QUERY = gql(`
  query ScoutNodes($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    scoutNodes(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        nodeId
        status
        scope
        ecsTaskArn
        cpuUsagePercentage
        memoryUsagePercentage
        lastHeartbeatAt
        createdAt
        updatedAt
      }
    }
  }
`);

type ScoutNodeRow = ScoutNodesQuery["scoutNodes"]["items"][number];

const percentFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

function formatPercent(value: number | null | undefined): string {
  if (typeof value !== "number") {
    return "";
  }
  return `${percentFormatter.format(value)}%`;
}

export function ScoutNodes() {
  const columns: OmnisearchGridColDef<ScoutNodeRow>[] = useMemo(
    () => [
      {
        field: "nodeId",
        headerName: "Node ID",
        flex: 1.4,
        minWidth: 220,
        filterKeyType: "text",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 130,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ScoutNodeStatus),
        renderCell({ value }) {
          return value ? <Chip size="small" label={value} /> : "";
        },
      },
      {
        field: "scope",
        headerName: "Scope",
        flex: 1,
        minWidth: 160,
        filterKeyType: "text",
      },
      {
        field: "lastHeartbeatAt",
        headerName: "Heartbeat",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "cpuUsagePercentage",
        headerName: "CPU",
        flex: 0.5,
        minWidth: 100,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatPercent(value);
        },
      },
      {
        field: "memoryUsagePercentage",
        headerName: "Memory",
        flex: 0.5,
        minWidth: 110,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatPercent(value);
        },
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "ecsTaskArn",
        headerName: "ECS Task ARN",
        flex: 1.8,
        minWidth: 260,
        filterKeyType: "text",
      },
      {
        field: "id",
        headerName: "ID",
        flex: 1.2,
        minWidth: 220,
        filterKeyType: "text",
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<ScoutNodesQuery, ScoutNodesQueryVariables, ScoutNodeRow>
        isURLDriven
        persistenceKey="observe-scout-nodes"
        columns={columns}
        dataQuery={SCOUT_NODES_QUERY}
        initialPageSize={50}
        initialSortModel={[{ field: "lastHeartbeatAt", sort: "desc" }]}
        getItems={(data) => data.scoutNodes.items}
        getCount={(data) => data.scoutNodes.count}
        noDataMessage="No Scout nodes found."
        gridSx={{
          minHeight: "500px",
        }}
      />
    </Stack>
  );
}
